/* eslint-disable no-nested-ternary */
import { GatsbyImage } from 'gatsby-plugin-image';
import { connectAutoComplete } from 'react-instantsearch-dom';
import styled from 'styled-components';
import SimpleBar from 'simplebar-react';
import 'simplebar/dist/simplebar.min.css';
import { useIntl } from 'react-intl';
import { SearchIcon } from '../../vectors/Icons';
import { easeInOutSine, fadeInAnim, fadeOutAnim, unMountAfterAnim } from '../../styles/animations';
import { Navigator } from '../../LanguageHelpers/Navigator';
import { usePageLocale } from '../../../hooks/usePageLocale';

const Wrapper = styled.div`
  width: 700px;
  display: grid;
  row-gap: var(--gap-5);
  --padding-offset: calc(32px + var(--gap-5));
  --inner-padding: 16px var(--padding-offset) 16px var(--padding-offset);
  position: absolute;
  top: var(--gap-4);
  animation: ${({ $autoComplete }) =>
      $autoComplete ? fadeInAnim : fadeOutAnim}
    300ms ${easeInOutSine} forwards;

  @media screen and (max-width: 768px) {
    width: 100%;
    top: 0;
    row-gap: 0;
    --inner-padding: 27.6px var(--padding-offset) 27.6px var(--padding-offset);
  }
`;

const InputFieldMask = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 5px 20px var(--blue-1);

  & svg {
    position: absolute;
    left: 0;
    margin-left: var(--gap-5);

    & path {
      fill: var(--blue-2);
    }
  }
`;

const InputField = styled.input`
  padding: var(--inner-padding);
  width: 100%;
  background: white;
  border: none;
  outline: none;
  border-radius: var(--gap-7);

  &::placeholder {
    font-size: var(--font-size-3);
  }

  @media screen and (max-width: 768px) {
    border-radius: 0;
    border: 1px solid #ececec;

    &::placeholder {
      font-size: var(--font-size-2);
    }
  }
`;

const CloseInputField = styled.button`
  background: none;
  padding: var(--gap-5);
  font-size: var(--font-size);
  font-weight: 500;
  position: absolute;
  right: 0;
  margin: 0;
  border: none;
  color: var(--blue-2);
`;

// Hits

const HitsList = styled.ul`
  list-style-type: none;
  border-radius: var(--gap-7);
  box-shadow: 0 5px 20px var(--blue-1);

  @media screen and (max-width: 768px) {
    box-shadow: none;
  }
`;

const Hit = styled(Navigator)`
  --img-width: 60px;
  display: grid;
  padding: var(--gap-6) var(--gap-5);
  grid-template-columns: calc(var(--img-width) + var(--gap-5)) 1fr;
  align-items: center;
  transition: background 100ms ease-in;
  cursor: pointer;

  @media (hover: hover) {
    &:hover {
      background: var(--gold-3);
      border-radius: var(--gap-7);
    }
  }

  &:nth-of-type(1),
  &:last-child {
    border-radius: var(--gap-7);
  }
`;

const HitImage = styled(GatsbyImage)`
  width: var(--img-width);
  height: var(--img-width);

  & img {
    border-radius: var(--img-width);
  }
`;

const HitContent = styled.div`
  display: grid;
`;

const HitCategory = styled.h2`
  font-size: var(--font-size-micro);
  color: var(--gold-2);
  font-weight: 600;
`;

const HitProduct = styled.h1`
  font-size: var(--font-size-2);
  color: var(--blue-2);
  line-height: 1.2;

  @media screen and (max-width: 480px) {
    font-size: var(--font-size-1);
  }
`;

const HitTag = styled.span`
  font-size: var(--font-size-micro);
  color: var(--blue-1-alpha);
  font-weight: 500;
  text-transform: uppercase;
`;

const AutoComplete = ({
  hits,
  currentRefinement,
  refine,
  closePopup,
  $autoComplete,
  setAutoComplete,
  inputRef,
}) => {
  const { locale } = usePageLocale();
  const intl = useIntl();

  return (
    <Wrapper
      $autoComplete={$autoComplete}
      onAnimationEnd={() => unMountAfterAnim($autoComplete, setAutoComplete)}
    >
      <InputFieldMask>
        <InputField
          placeholder={intl.formatMessage({ id: 'search' })}
          type="search"
          value={currentRefinement}
          ref={inputRef}
          onChange={(event) => refine(event.currentTarget.value)}
        />
        <SearchIcon />
        <CloseInputField onClick={() => closePopup()}>
          {intl.formatMessage({ id: 'close' })}
        </CloseInputField>
      </InputFieldMask>
      {!currentRefinement ? null : (
        <SimpleBar
          forceVisible
          autoHide={false}
          className="simplebarAutoComplete"
        >
          <HitsList>
            {hits.map(
              ({
                objectID,
                productCategory,
                regimiAlimentari,
                titolo,
                fakeObjectID,
                mainImage,
              }) => (
                <li key={objectID}>
                  <Hit aria-label={titolo} recordId={fakeObjectID ?? objectID.replace(`_${locale}`, '')}>
                    {mainImage?.thumbnailImageData && (
                      <HitImage
                        image={mainImage.thumbnailImageData}
                        alt={titolo}
                      />
                    )}
                    <HitContent>
                      <HitCategory>{productCategory?.title}</HitCategory>
                      <HitProduct>{titolo}</HitProduct>
                      {regimiAlimentari[0] && regimiAlimentari[1] ? (
                        <HitTag>{`${regimiAlimentari[0]} - ${regimiAlimentari[1]}`}</HitTag>
                      ) : regimiAlimentari[0] && !regimiAlimentari[1] ? (
                        <HitTag>{regimiAlimentari[0]}</HitTag>
                      ) : null}
                    </HitContent>
                  </Hit>
                </li>
              )
            )}
          </HitsList>
        </SimpleBar>
      )}
    </Wrapper>
  );
};

export const CustomAutocomplete = connectAutoComplete(AutoComplete);
